import { HttpClient, HttpParams } from '@angular/common/http'
import { Component, computed, effect, inject, input } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faCamera, faLocationCrosshairs, faSpinner, faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'

import { AuthenticationService, ListItem } from '../../../../shared'
import { getCurrentPosition, googleMapsUrl } from '../../../../shared/utility/geolocation'
import {
    TIRE_EQUIPMENT_CLASSES,
    TIRE_PROBLEM_TYPES,
    TIRE_SIDES,
    TireReport,
    UTAH_COUNTIES,
} from '../tire-report.model'

interface EquipmentTireConfigRow {
    axleNumber: number
    side: string
    tireSize: string
    positionLabel?: string
}

interface JobAddressResult {
    mailAddress?: string
    shipCity?: string
    shipState?: string
    hasCompleteAddress?: boolean
}

@Component({
    selector: 'app-tire-report-form',
    templateUrl: './tire-report-form.component.html',
    styleUrls: ['./tire-report-form.component.css'],
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent],
})
export class TireReportFormComponent {
    tireReport = input.required<TireReport>()
    jobOptions = input<ListItem<string>[]>([])
    employeeOptions = input<ListItem<number>[]>([])
    equipmentOptions = input<ListItem<string>[]>([])

    private http = inject(HttpClient)
    private auth = inject(AuthenticationService)

    faCamera = faCamera
    faLocationCrosshairs = faLocationCrosshairs
    faSpinner = faSpinner
    faX = faX
    progress = 0
    isLocating = false
    locationError = ''
    private phonePrefillAttempted = false

    equipmentClassOptions = TIRE_EQUIPMENT_CLASSES.map(c => new ListItem(c.value, c.label))
    problemTypeOptions = TIRE_PROBLEM_TYPES.map(p => new ListItem(p.value, p.label))
    sideOptions = TIRE_SIDES.map(s => new ListItem(s.value, s.label))

    axleConfigPreview: EquipmentTireConfigRow[] = []
    jobLocationHint = ''

    operatorOptions = computed(() => {
        const options = this.employeeOptions()
        const hrref = this.auth.hrref
        if (!hrref || options.some(x => x.value === hrref)) {
            return options
        }
        const label = this.auth.displayName() || 'Current user'
        return [...options, new ListItem<number>(hrref, label)]
    })

    constructor() {
        effect(() => {
            const report = this.tireReport()
            if (report.equipmentId) {
                this.loadTireConfig(report.equipmentId)
            }
            this.prefillPhoneIfNeeded()
        })
    }

    locationMapUrl(): string {
        const report = this.tireReport()
        if (report.locationLatitude == null || report.locationLongitude == null) {
            return ''
        }
        return googleMapsUrl(report.locationLatitude, report.locationLongitude)
    }

    onEquipmentChanged() {
        const report = this.tireReport()
        if (report.equipmentId) {
            this.loadTireConfig(report.equipmentId)
            this.refreshEquipmentClass(report.equipmentId)
        } else {
            this.axleConfigPreview = []
            report.equipmentClass = ''
        }
        this.refreshResolvedSize()
    }

    onPositionChanged() {
        this.refreshResolvedSize()
    }

    onJobChanged() {
        const report = this.tireReport()
        const job = report.job?.trim()
        if (!job) {
            this.jobLocationHint = ''
            report.county = ''
            return
        }

        const params = new HttpParams().set('jobId', job)
        this.http.get<JobAddressResult>('api/VpJob/GetJobAddress', { params }).subscribe({
            next: result => {
                const city = result?.shipCity?.trim() ?? ''
                const state = result?.shipState?.trim() ?? ''
                this.jobLocationHint = [city, state].filter(x => x).join(', ')
                report.county = this.matchCountyFromCity(city)
            },
            error: () => {
                this.jobLocationHint = ''
                report.county = ''
            },
        })
    }

    async useMyLocation() {
        if (this.isLocating) return

        this.isLocating = true
        this.locationError = ''
        try {
            const position = await getCurrentPosition()
            const report = this.tireReport()
            report.locationLatitude = position.latitude
            report.locationLongitude = position.longitude
            report.locationCapturedOn = new Date()
        } catch (error: any) {
            this.locationError = error?.message || 'Unable to get your current location.'
        } finally {
            this.isLocating = false
        }
    }

    clearCapturedLocation() {
        const report = this.tireReport()
        report.locationLatitude = null
        report.locationLongitude = null
        report.locationCapturedOn = null
        this.locationError = ''
    }

    private matchCountyFromCity(city: string): string {
        if (!city) {
            return ''
        }
        const normalized = city.trim().toLowerCase()
        const exact = UTAH_COUNTIES.find(c => c.toLowerCase() === normalized)
        if (exact) {
            return exact
        }
        const prefix = UTAH_COUNTIES.find(c => normalized.startsWith(c.toLowerCase()))
        return prefix ?? ''
    }

    private prefillPhoneIfNeeded() {
        const report = this.tireReport()
        if (this.phonePrefillAttempted || report.id || report.submitterPhone) {
            return
        }

        this.phonePrefillAttempted = true
        this.http.get<{ phone: string }>('api/EquipmentReport/CurrentUserPhone').subscribe({
            next: result => {
                const current = this.tireReport()
                if (!current.submitterPhone && result?.phone) {
                    current.submitterPhone = result.phone
                }
            },
        })
    }

    private refreshEquipmentClass(equipment: string) {
        const params = new HttpParams().set('equipment', equipment)
        this.http.get<{ equipmentClass: string }>('api/EquipmentTireConfig/ClassForEquipment', { params }).subscribe({
            next: result => {
                this.tireReport().equipmentClass = result?.equipmentClass ?? ''
            },
        })
    }

    private loadTireConfig(equipment: string) {
        const params = new HttpParams().set('equipment', equipment)
        this.http.get<EquipmentTireConfigRow[]>('api/EquipmentTireConfig/ForEquipment', { params }).subscribe({
            next: rows => {
                this.axleConfigPreview = rows ?? []
            },
        })
    }

    private refreshResolvedSize() {
        const report = this.tireReport()
        if (!report.equipmentId || !report.axleNumber || !report.side) {
            report.resolvedTireSize = ''
            return
        }

        const params = new HttpParams()
            .set('equipment', report.equipmentId)
            .set('axleNumber', report.axleNumber.toString())
            .set('side', report.side)

        this.http.get<{ tireSize: string }>('api/EquipmentTireConfig/ResolveSize', { params }).subscribe({
            next: result => {
                report.resolvedTireSize = result?.tireSize ?? ''
            },
        })
    }

    async onPhotoSelected(event: Event) {
        const input = event.target as HTMLInputElement
        const files = input.files
        if (!files?.length) return

        const report = this.tireReport()
        for (const file of Array.from(files)) {
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: p => (this.progress = p),
            }
            const compressed = await imageCompression(file, options)
            ;(compressed as any)['url'] = await imageCompression.getDataUrlFromFile(compressed)
            report.images.push(compressed)
        }
        input.value = ''
    }

    removePhoto(index: number) {
        this.tireReport().images.splice(index, 1)
    }

    async onReceiptSelected(event: Event) {
        const input = event.target as HTMLInputElement
        const files = input.files
        if (!files?.length) return

        const report = this.tireReport()
        for (const file of Array.from(files)) {
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
            }
            const compressed = file.type.startsWith('image/')
                ? await imageCompression(file, options)
                : file
            if (compressed instanceof Blob && !(compressed as any).url) {
                ;(compressed as any)['url'] = file.type.startsWith('image/')
                    ? await imageCompression.getDataUrlFromFile(compressed as File)
                    : null
            }
            ;(compressed as any)['name'] = file.name
            report.receiptImages.push(compressed)
        }
        input.value = ''
    }

    removeReceipt(index: number) {
        this.tireReport().receiptImages.splice(index, 1)
    }
}
